/**
 * EDNL tabs and demo APIs scope data by workspace_id. When the user has not
 * picked a workspace in the app, we still need a non-empty id so list/create
 * calls stay consistent (in-memory GraphBuilder routes filter by workspace).
 */
export const DEFAULT_EDNL_WORKSPACE_ID = 'default';

export function getEdnlWorkspaceId() {
  try {
    const ws = localStorage.getItem('selectedWorkspace');
    if (ws) {
      const parsed = JSON.parse(ws);
      const id = parsed?.id ?? parsed?.workspaceId ?? parsed;
      if (id != null && String(id).trim() !== '') {
        return String(id).trim();
      }
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_EDNL_WORKSPACE_ID;
}

/**
 * Persist the user's active workspace so every EDNL tab (and anything else
 * reading `localStorage.selectedWorkspace`) picks up the same id on next
 * mount / refresh. Shape matches what `getEdnlWorkspaceId` already reads.
 */
export function setEdnlWorkspaceId(workspaceId, workspaceName) {
  try {
    if (!workspaceId) {
      localStorage.removeItem('selectedWorkspace');
      return;
    }
    localStorage.setItem(
      'selectedWorkspace',
      JSON.stringify({
        id: workspaceId,
        workspaceId,
        name: workspaceName || null,
        workspaceName: workspaceName || null,
      }),
    );
  } catch {
    /* ignore: private mode, quota */
  }
}

/**
 * Read the friendly workspace name (if any) saved alongside the id by
 * ``setEdnlWorkspaceId``. Returns null when localStorage is empty, the entry
 * is unparseable, or the name was not stored.
 */
export function getEdnlWorkspaceName() {
  try {
    const ws = localStorage.getItem('selectedWorkspace');
    if (ws) {
      const parsed = JSON.parse(ws);
      const name = parsed?.name ?? parsed?.workspaceName;
      if (name && String(name).trim() !== '') return String(name).trim();
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Render helper for displaying a workspace identifier to the user. Examples:
 *   formatWorkspaceLabel('d428b6b4-...', 'Acme Bank')  -> 'Acme Bank (d428b6b4)'
 *   formatWorkspaceLabel('d428b6b4-...', null)         -> 'd428b6b4'
 *   formatWorkspaceLabel(null, null)                   -> '(none)'
 */
export function formatWorkspaceLabel(id, name) {
  const shortId = id ? String(id).slice(0, 8) : null;
  if (name && shortId) return `${name} (${shortId})`;
  if (shortId) return shortId;
  return '(none)';
}

/**
 * Shared UUID v1-v5 shape check. EDNL tabs use this to gate API calls that
 * require a real workspace id (the literal fallback string 'default' must
 * not be sent to the backend).
 */
export function isUuidString(s) {
  return (
    typeof s === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim())
  );
}
